import React from 'react';
import { motion } from 'framer-motion';
import { X, Trophy, BookOpen } from 'lucide-react';

interface ExistingTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: {
    assignedLevel: string;
    levelDescription: string;
    startersScore: number;
    moversScore: number;
    flyersScore: number;
    totalScore: number;
  };
}

const ExistingTestModal: React.FC<ExistingTestModalProps> = ({ isOpen, onClose, results }) => {
  if (!isOpen) return null;

  const getLevelInfo = (level: string) => {
    if (level.includes('A2')) {
      return { 
        emoji: '🌟', 
        color: '#45b7d1', 
        bgColor: '#45b7d120',
        message: '¡Excelente! Tienes un nivel intermedio de inglés. ¡Sigue practicando para llegar al nivel avanzado! 🌟'
      };
    } else if (level.includes('A1')) {
      return { 
        emoji: '🎯', 
        color: '#4ecdc4', 
        bgColor: '#4ecdc420',
        message: '¡Muy bien! Tienes un nivel básico de inglés. ¡Con más práctica llegarás al intermedio! 🎯'
      };
    } else if (level.includes('Pre-A1')) {
      return { 
        emoji: '🚀', 
        color: '#ff6b6b', 
        bgColor: '#ff6b6b20',
        message: '¡Perfecto! Estás en el nivel principiante. ¡Cada día aprenderás algo nuevo! 🚀'
      };
    } else {
      return { 
        emoji: '📚', 
        color: '#666', 
        bgColor: '#66620',
        message: '¡No te preocupes! Todos empezamos desde el principio. ¡Con dedicación llegarás lejos! 📚'
      };
    }
  };

  const levelInfo = getLevelInfo(results.assignedLevel);
  const percentage = Math.round((results.totalScore / 20) * 100);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '30px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        {/* Botón de cerrar */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '5px'
          }}
        >
          <X />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>
            {levelInfo.emoji}
          </div>
          <h2 style={{ color: '#333', marginBottom: '10px', fontSize: '2rem' }}>
            ¡Ya realizaste el test de ubicación! 🎉
          </h2>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Aquí están tus resultados obtenidos:
          </p>
        </div>

        {/* Resultado principal */}
        <div
          style={{
            background: `linear-gradient(45deg, ${levelInfo.bgColor}, rgba(255,255,255,0.9))`,
            border: `3px solid ${levelInfo.color}`,
            borderRadius: '15px',
            padding: '25px',
            textAlign: 'center',
            marginBottom: '25px'
          }}
        >
          <h3 style={{ color: levelInfo.color, marginBottom: '10px', fontSize: '1.8rem' }}>
            {results.assignedLevel}
          </h3>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            {results.levelDescription}
          </p>
          <div style={{
            background: levelInfo.color,
            color: 'white',
            padding: '10px 20px',
            borderRadius: '25px',
            fontSize: '18px',
            fontWeight: 'bold',
            display: 'inline-block'
          }}>
            Puntuación: {results.totalScore}/20 ({percentage}%)
          </div>
        </div>

        {/* Estadísticas detalladas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '15px',
          marginBottom: '25px'
        }}>
          <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '10px' }}>
            <div style={{ fontSize: '2rem', marginBottom: '5px' }}>🚀</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ff6b6b' }}>
              {results.startersScore}/7
            </div>
            <div style={{ color: '#666', fontSize: '12px' }}>Pre-A1</div>
          </div>

          <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '10px' }}>
            <div style={{ fontSize: '2rem', marginBottom: '5px' }}>🎯</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {results.moversScore}/7
            </div>
            <div style={{ color: '#666', fontSize: '12px' }}>A1</div>
          </div>

          <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '10px' }}>
            <div style={{ fontSize: '2rem', marginBottom: '5px' }}>🌟</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#45b7d1' }}>
              {results.flyersScore}/6
            </div>
            <div style={{ color: '#666', fontSize: '12px' }}>A2</div>
          </div>
        </div>

        {/* Mensaje motivacional */}
        <div style={{
          background: `linear-gradient(45deg, ${levelInfo.bgColor}, rgba(255,255,255,0.8))`,
          border: `2px solid ${levelInfo.color}`,
          borderRadius: '15px',
          padding: '20px',
          marginBottom: '25px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#333', margin: 0, fontSize: '16px', lineHeight: '1.6' }}>
            {levelInfo.message}
          </p>
        </div>

        {/* Botones de acción */}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => window.location.href = 'https://children.ibcidiomas.com'}
            style={{
              background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <BookOpen size={20} />
            Continuar Aprendiendo
          </button>

        </div>

        {/* Información adicional */}
        <div style={{
          marginTop: '20px',
          padding: '15px',
          background: 'rgba(78, 205, 196, 0.1)',
          borderRadius: '10px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#666', margin: 0, fontSize: '14px' }}>
            <Trophy style={{ display: 'inline', marginRight: '8px', color: '#4ecdc4' }} />
            <strong>¡Recibiste un email con tus resultados!</strong>
            <br />
            <small>Revisa tu bandeja de entrada para el certificado de ubicación.</small>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ExistingTestModal;
